import { Product } from '@interfaces';
import { CurrencyFormatter } from '@utils/formatters/CurrencyFormatter';
import { format } from 'date-fns';
import { PriceComparatorInfoModal } from './PriceComparatorInfoModal';
import { PriceComparatorTitleContainer, Title, TopContainer } from '../styles';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { ProductMainCard } from '../styles';
import {
  Row,
  PriceText,
  ProviderNameDiv,
  Column,
  SalePriceText,
  InfoText,
  BottomContainer,
  BottomTextNoInfo,
} from './PriceComparator.styles';

enum PharmacyNames {
  farmaciaAhumada = 'Ahumada',
  salcobrand = 'Salcobrand',
  cruzverde = 'Cruz Verde',
  drsimi = 'Dr. Simi',
  meki = 'Meki',
}
interface ProvidersPriceInfo {
  name: PharmacyNames;
  price: number;
  salePrice?: number;
  ranking?: number;
  finalPrice: number;
}
const formatComparatorRows = (product: Product): ProvidersPriceInfo[] => {
  if (!product) return [];
  const mekiPrice = product.discount?.price
    ? (product.discount?.listPrice as number) ?? product.price
    : product.price;
  const allProvidersInfo = [
    ...(mekiPrice &&
    !['SOLD_OUT', 'UNAVAILABLE', 'HIDDEN'].includes(
      product.availability?.status,
    )
      ? [
          {
            name: PharmacyNames.meki,
            price: mekiPrice,
            salePrice: product.discount?.price,
            finalPrice: product.discount?.price ?? mekiPrice,
            ranking: -1,
          },
        ]
      : []),
    ...(product.externalPrices
      ? product.externalPrices?.filter(Boolean).map((e) => ({
          name: PharmacyNames[e.seller as keyof typeof PharmacyNames],
          price: e.amount,
          salePrice: e.saleAmount,
          finalPrice: e.saleAmount ?? e.amount,
          ranking: -1,
        }))
      : []),
  ];
  //ordenar por finalPrice
  allProvidersInfo.sort((a, b) => {
    return (
      (a.finalPrice === null ? 1 : 0) - (b.finalPrice === null ? 1 : 0) ||
      a.finalPrice - b.finalPrice
    );
  });
  const pricesRanking: number[] = Array.from(
    new Set(allProvidersInfo.map((provider) => provider.finalPrice)),
  );
  const allProvidersInfoRanked = allProvidersInfo.map(
    (provider, index, providersList) => {
      const currentPrice = provider.finalPrice;
      if (index > 0) {
        const previousProvider = providersList[index - 1] as ProvidersPriceInfo;
        const previousPrice = previousProvider.finalPrice;
        if (currentPrice === previousPrice) {
          return {
            ...provider,
            ranking: pricesRanking.indexOf(currentPrice) + 1,
          };
        }
      }
      return { ...provider, ranking: index + 1 };
    },
  );
  return allProvidersInfoRanked;
};

const currencyFormatter = new CurrencyFormatter();

interface PriceComparatorProps {
  product: Product;
}
export const PriceComparator = ({ product }: PriceComparatorProps) => {
  const rows = formatComparatorRows(product);
  const lastUpdateString = product.externalPrices
    ?.filter(Boolean)
    .reduce((min, row) => {
      if (!min) return row.lastUpdate;
      return min > row.lastUpdate ? row.lastUpdate : min;
    }, '');
  const lastUpdate = lastUpdateString ? new Date(lastUpdateString) : null;
  const lastUpdateDate = lastUpdate ? format(lastUpdate, 'dd.MM.yy') : null;
  const lastUpdateTime = lastUpdate ? format(lastUpdate, 'hh:ii') : null;
  const lastUpdateDateAndTime =
    lastUpdateDate && lastUpdateTime
      ? `${lastUpdateDate} a las ${lastUpdateTime}hrs`
      : null;
  const notFoundPrices = Object.values(PharmacyNames)
    .filter((value) => {
      return !rows.some((e) => e.name === value);
    })
    .join(', ');

  return (
    <ProductMainCard>
      <TopContainer>
        <PriceComparatorTitleContainer>
          <Title>Precios online</Title>
          {lastUpdate && (
            <InfoText>
              Actualizados el {lastUpdateDate} a las {lastUpdateTime} hrs.
            </InfoText>
          )}
        </PriceComparatorTitleContainer>
        <PriceComparatorInfoModal lastUpdate={lastUpdateDateAndTime} />
      </TopContainer>
      {rows.map((row) => {
        const firstRanked = row.ranking == 1 && Boolean(row.finalPrice);
        return (
          <Row
            key={row.name}
            bold={true}
            firstRanked={firstRanked}
            bottomTextInfo={Boolean(notFoundPrices)}
          >
            <ProviderNameDiv>
              {firstRanked ? <WorkspacePremiumIcon /> : `${row.ranking}.`}{' '}
              {row.name}
            </ProviderNameDiv>
            <Column>
              {row.salePrice && row.salePrice < row.price && (
                <SalePriceText>
                  {currencyFormatter.format(row.price)}
                </SalePriceText>
              )}
              <PriceText
                bold={row.name === 'Meki'}
                discountColor={row.name === 'Meki' && row.salePrice > 0}
                firstRanked={firstRanked}
              >
                {row.price || row.salePrice
                  ? currencyFormatter.format(
                      row.salePrice && row.salePrice < row.price
                        ? row.salePrice
                        : row.price,
                    )
                  : 'No lo encontramos'}
              </PriceText>
            </Column>
          </Row>
        );
      })}
      {notFoundPrices && (
        <BottomContainer>
          <BottomTextNoInfo>
            No se encontraron precios en: {notFoundPrices}
          </BottomTextNoInfo>
        </BottomContainer>
      )}
    </ProductMainCard>
  );
};
